import axios from "axios";
import axiosInstance from "./axios.instance";
import { setAuthToken, storeToken } from "./auth.utils";

// Normalised to exactly one trailing slash — every call below appends `api/v1/eklink/...`
// with no leading slash, so a base without one made sign-in itself request
// `...onrender.comapi/v1/eklink/user/auth/login`. See the note in services/api.service.js.
const authApiUrl = `${(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')}/`;

export const register = async (userData) => {
  let result;
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/signUp`, userData);
    result = response.data;
  } catch (error) {
    result = error;
  }
  return result;
}

export const loginSentOtp = async (userData) => {
  let result;
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/auth/login`, userData);
    result = response.data;
  } catch (error) {
    result = error;
  }
  return result;
}

export const login = async (data) => {
  let result;
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/auth/verifyOtp`, data);
    const responseData = response.data;

    // The post-login destination arrives as data.user.personaAppUrl and the redirect itself is
    // performed by login.jsx (via the authorization code, not a raw token in the query string).
    // For Ecosystem Integrators the backend resolves that URL from the integrator's type — one of
    // Area / Crop / Initiative / Finance Based — each configured in the eKutir superadmin.
    storeLoginSession(responseData);

    result = responseData;
  } catch (error) {
    result = error;
  }
  return result;
}

// Store authentication data for local Ekutir ecosystem users, after any successful sign-in
// (OTP or PIN): both answer with the same data.user + authorization code.
const storeLoginSession = (responseData) => {
  const ecosystemIntegratorName = responseData?.data?.user?.organisationDetail?.ecosystemIntegrator?.name;

  if (ecosystemIntegratorName === 'Ekutir' || !ecosystemIntegratorName) {
    // verifyOtp returns no accessToken/refreshToken any more — the tokens are exchanged for
    // the authorization code by the destination app — so these are usually absent. Writing
    // them unguarded stored the literal string "undefined", which then rode along on every
    // later request as `Bearer undefined`.
    storeToken("accessToken", responseData.data.accessToken);
    storeToken("refreshToken", responseData.data.refreshToken);
    localStorage.setItem("userData", JSON.stringify(responseData.data.user));
    localStorage.setItem("tokenDomain", responseData.data.domain || '');
    setAuthToken();
  }
};

// PIN login steps (feature/pin-login). Plain axios on purpose: the shared instance treats every
// 401 as an expired session and tries to refresh it, but here a 401 is just a wrong PIN. Every
// answer, error or not, is returned as the backend's own {statusCode, message, data} body.
const pinPost = async (path, body) => {
  try {
    const response = await axios.post(`${authApiUrl}api/v1/eklink/user/auth/pin/${path}`, body, {
      validateStatus: () => true,
    });
    if (response.data && typeof response.data === 'object') {
      return { statusCode: response.status, ...response.data };
    }
    return { statusCode: response.status, message: 'Unable to continue right now. Please try again.' };
  } catch (error) {
    return { statusCode: 0, message: 'Unable to reach the server. Please check your connection and try again.' };
  }
};

const withSession = (result) => {
  if (result.statusCode === 200 && result.data?.user) {
    storeLoginSession(result);
  }
  return result;
};

/** {phoneNumber, countryCode, pin} → the same handover as verifyOtp, or 401/409/423 with data.nextStep. */
export const pinLogin = async (data) => withSession(await pinPost('login', data));

/** {phoneNumber, countryCode, purpose: 'PIN_SETUP' | 'PIN_RESET'} */
export const pinSendOtp = (data) => pinPost('otp', data);

/** {phoneNumber, countryCode, otp, purpose} → {pinTicket} */
export const pinVerifyOtp = (data) => pinPost('otp/verify', data);

/** {pinTicket, pin, pinConfirm} → the same handover as verifyOtp */
export const pinSet = async (data) => withSession(await pinPost('set', data));

/**
 * {code, userId, destinationId} → a new authorization code for the app picked on the page after
 * sign-in, as data.authCode with data.user.personaAppUrl. Plain axios for the same reason as the PIN
 * calls: every answer comes back as the backend's own {statusCode, message, data} body.
 */
export const loginHandover = async (body) => {
  try {
    const response = await axios.post(`${authApiUrl}api/v1/eklink/user/auth/handover`, body, {
      validateStatus: () => true,
    });
    if (response.data && typeof response.data === 'object') {
      return { statusCode: response.status, ...response.data };
    }
    return { statusCode: response.status, message: 'Unable to continue right now. Please try again.' };
  } catch (error) {
    return { statusCode: 0, message: 'Unable to reach the server. Please check your connection and try again.' };
  }
};

export const eklinkSignupSendOtp = async (data) => {
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/signUp/sendOtp`, data);
    return response.data;
  } catch (error) {
    // axios.instance.js's response interceptor already normalizes any rejection into a
    // plain {statusCode, message} object (it never has a .response property), so surface
    // it directly instead of falling back to a generic message and discarding the real
    // backend reason (e.g. "An account with this phone number already exists").
    return error;
  }
}

export const eklinkSignupVerifyOtp = async (data) => {
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/signUp/verifyOtp`, data);
    return response.data;
  } catch (error) {
    return error;
  }
}

export const logout = async (data) => {
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/auth/logout`, data);
    localStorage.clear();
    sessionStorage.clear();
    return response.data; 
  } catch (error) {
    throw error; 
  }
};

export const validationApi = async (data) => {
  let result;
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/signUp/validateEmailPhoneNumber`, data);
    result = response.data;
  } catch (error) {
    result = error;
  }
  return result;
}

export const validateEmailApi = async (data) => {
  let result;
  try {
    const response = await axiosInstance.post(`${authApiUrl}api/v1/eklink/user/signUp/validateEmail`, data);
    result = response.data;
  } catch (error) {
    result = error;
  }
  return result;
}
